import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import EmptyState from './EmptyState';

interface ExhibitorData {
  exhibitorId: number;
  exhibitorName: string;
  count: number;
}

export interface TopExhibitorsTableProps {
  title: string;
  data: ExhibitorData[];
  countLabel: string;
  emptyMessage?: string;
  maxItems?: number;
}

const getMedalColor = (rank: number): string => {
  switch (rank) {
    case 1:
      return '#fbbf24'; // Gold
    case 2:
      return '#9ca3af'; // Silver
    case 3:
      return '#cd7f32'; // Bronze
    default:
      return 'transparent';
  }
};

const TopExhibitorsTable: React.FC<TopExhibitorsTableProps> = ({
  title,
  data,
  countLabel,
  emptyMessage = 'No exhibitors to display',
  maxItems = 10,
}) => {
  const displayData = data.slice(0, maxItems);

  return (
    <Card
      sx={{
        height: '100%',
        boxShadow:
          '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb',
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          sx={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: '#111827',
            mb: 2,
          }}
        >
          {title}
        </Typography>

        {displayData.length === 0 ? (
          <EmptyState message={emptyMessage} height={200} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: '#6b7280',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      borderBottom: '2px solid #e5e7eb',
                      width: 60,
                    }}
                  >
                    Rank
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: '#6b7280',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      borderBottom: '2px solid #e5e7eb',
                    }}
                  >
                    Exhibitor
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: '#6b7280',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      borderBottom: '2px solid #e5e7eb',
                      width: 100,
                    }}
                  >
                    {countLabel}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.map((item, index) => {
                  const rank = index + 1;
                  const medalColor = getMedalColor(rank);

                  return (
                    <TableRow
                      key={item.exhibitorId}
                      sx={{
                        '&:hover': { backgroundColor: '#f9fafb' },
                        '&:last-child td': { borderBottom: 0 },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {rank <= 3 ? (
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: medalColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <TrophyIcon
                                sx={{ fontSize: 16, color: 'white' }}
                              />
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#6b7280',
                              }}
                            >
                              #{rank}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: '#dbeafe',
                              color: '#3b82f6',
                            }}
                          >
                            <BusinessIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: '#111827',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 250,
                            }}
                          >
                            {item.exhibitorName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={item.count}
                          size="small"
                          sx={{
                            backgroundColor:
                              rank === 1
                                ? '#dcfce7'
                                : rank === 2
                                  ? '#dbeafe'
                                  : rank === 3
                                    ? '#fef3c7'
                                    : '#f3f4f6',
                            color:
                              rank === 1
                                ? '#16a34a'
                                : rank === 2
                                  ? '#2563eb'
                                  : rank === 3
                                    ? '#d97706'
                                    : '#6b7280',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default TopExhibitorsTable;
